import { forwardRef } from 'react';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

/**
 * F4.1.1.a — as classes eram concatenadas por template string, então o
 * `className` do consumidor entrava ANTES no atributo e perdia para a variante
 * (`className="text-white"` sobre `variant="outline"` continuava cinza).
 * `cn()` usa tailwind-merge: a última classe conflitante vence, que é o que
 * quem chama espera. A API pública é a mesma — Home, Header, Contato, Unidades
 * e ViverMais já dependem dela.
 */
const VARIANTES = {
  // O hover era `hover:bg-sky-700` (azul) sobre `bg-santa-green` (verde): o
  // botão principal do site trocava de cor de marca ao passar o mouse.
  primary:
    'bg-santa-green text-white hover:bg-santa-dark-light shadow-premium hover:shadow-premium-hover',
  ghost: 'text-slate-700 hover:bg-slate-100',
  outline: 'border-2 border-slate-200 bg-transparent text-slate-700 hover:bg-slate-50',
  whatsapp: 'bg-[#25D366] text-white hover:bg-[#22bf5b] shadow-premium hover:shadow-premium-hover',
  gold: 'bg-santa-gold text-santa-dark hover:bg-[#AD8E4B] shadow-premium hover:shadow-premium-hover',
  danger: 'bg-red-600 text-white hover:bg-red-700 shadow-premium hover:shadow-premium-hover',
} as const;

const TAMANHOS = {
  default: 'h-11 px-5 py-2',
  sm: 'h-9 rounded-md px-3 text-sm',
  lg: 'h-14 rounded-md px-8 text-lg',
  icon: 'h-11 w-11',
} as const;

const BASE =
  'inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-santa-green focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof VARIANTES;
  size?: keyof typeof TAMANHOS;
  href?: string;
  target?: string;
  rel?: string;
  /** Mostra spinner e bloqueia o clique. Evita pedido duplicado no checkout. */
  carregando?: boolean;
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'default',
      href,
      carregando = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const classes = cn(BASE, VARIANTES[variant], TAMANHOS[size], className);

    // No tamanho `icon` não há texto: o spinner substitui o ícone em vez de
    // se somar a ele, senão o conteúdo estoura o quadrado de 44px.
    const conteudo = carregando ? (
      <>
        <Spinner
          tamanho={size === 'lg' ? 'default' : 'sm'}
          rotulo=""
          className={size === 'icon' ? undefined : 'mr-2'}
        />
        {size === 'icon' ? null : children}
      </>
    ) : (
      children
    );

    if (href) {
      const anchorProps = props as React.AnchorHTMLAttributes<HTMLAnchorElement>;
      return (
        <a
          href={carregando ? undefined : href}
          className={cn(classes, carregando && 'pointer-events-none opacity-50')}
          ref={ref as React.Ref<HTMLAnchorElement>}
          aria-busy={carregando || undefined}
          aria-disabled={carregando || undefined}
          {...anchorProps}
        >
          {conteudo}
        </a>
      );
    }

    return (
      <button
        className={classes}
        ref={ref as React.Ref<HTMLButtonElement>}
        disabled={disabled || carregando}
        aria-busy={carregando || undefined}
        {...props}
      >
        {conteudo}
      </button>
    );
  },
);

Button.displayName = 'Button';

export { Button };
